import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { IconPicker } from './IconPicker';
import type { HelpCenterCategory, CreateCategoryRequest, UpdateCategoryRequest } from '@/types';

interface CategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateCategoryRequest | UpdateCategoryRequest) => Promise<void>;
  category?: HelpCenterCategory | null;
  parentId?: number | null;
  isSubmitting?: boolean;
  error?: string | null;
}

export function CategoryModal({
  isOpen,
  onClose,
  onSubmit,
  category,
  parentId,
  isSubmitting = false,
  error,
}: CategoryModalProps) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState<string | null>(null);
  const [isPublished, setIsPublished] = useState(true);
  const [autoGenerateSlug, setAutoGenerateSlug] = useState(true);

  // Generate slug from title
  const generateSlugFromTitle = (titleText: string): string => {
    return titleText
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Auto-generate slug when title changes (only if auto-generate is enabled and not editing)
  useEffect(() => {
    if (!category && autoGenerateSlug && title) {
      setSlug(generateSlugFromTitle(title));
    }
  }, [title, autoGenerateSlug, category]);

  useEffect(() => {
    if (category) {
      setTitle(category.title);
      setSlug(category.slug || '');
      setDescription(category.description || '');
      setIcon(category.icon || null);
      setIsPublished(category.is_published ?? false);
      setAutoGenerateSlug(false); // Don't auto-generate when editing
    } else {
      setTitle('');
      setSlug('');
      setDescription('');
      setIcon(null);
      setIsPublished(true);
      setAutoGenerateSlug(true);
    }
  }, [category, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      return;
    }

    const data = category
      ? ({
          title: title.trim(),
          slug: slug.trim() || null, // Send null if empty, server will generate from title
          description: description.trim() || undefined,
          icon: icon,
          is_published: isPublished,
        } as UpdateCategoryRequest)
      : ({
          parent_id: parentId ?? null,
          title: title.trim(),
          slug: slug.trim() || null, // Send null if empty, server will generate from title
          description: description.trim() || undefined,
          icon: icon,
          is_published: isPublished,
        } as CreateCategoryRequest);

    await onSubmit(data);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {category ? 'Edit Category' : 'Create Category'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Category title"
              required
              disabled={isSubmitting}
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label htmlFor="slug">URL Slug</Label>
              {!category && (
                <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoGenerateSlug}
                    onChange={(e) => setAutoGenerateSlug(e.target.checked)}
                    className="h-4 w-4"
                  />
                  Auto-generate from title
                </label>
              )}
            </div>
            <Input
              id="slug"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setAutoGenerateSlug(false);
              }}
              placeholder="category-slug"
              disabled={isSubmitting || (autoGenerateSlug && !category)}
              pattern="[a-z0-9_-]+"
              title="Slug can only contain lowercase letters, numbers, hyphens, and underscores"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Used in URLs
            </p>
          </div>
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Category description (optional)"
              rows={3}
              disabled={isSubmitting}
            />
          </div>
          <div>
            <Label htmlFor="icon">Icon</Label>
            <IconPicker
              value={icon}
              onChange={setIcon}
              disabled={isSubmitting}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="published">Published</Label>
              <p className="text-sm text-muted-foreground">
                {isPublished ? 'Visible to users' : 'Hidden from users (draft)'}
              </p>
            </div>
            <Switch
              id="published"
              checked={isPublished}
              onCheckedChange={setIsPublished}
              disabled={isSubmitting}
            />
          </div>
          {error && (
            <div className="text-sm text-destructive">{error}</div>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !title.trim()}>
              {isSubmitting ? 'Saving...' : category ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
